/**
 * Locked candidates -- pointing pairs/triples and box-line reduction.
 *
 * Both are one deduction stated over any two overlapping units: if every place
 * a digit can still go in unit A falls inside A's overlap with unit B, then the
 * digit is used up somewhere in that overlap, and can be removed from the rest
 * of B. Naming is the only difference -- "pointing" when the digit is locked
 * inside a box, "claiming" when it is locked inside a line.
 *
 * Stating it over units rather than over boxes-and-lines is what makes it work
 * unchanged for Jigsaw and other variants.
 */

import { EMPTY, cellName, type Digit } from "../grid";
import { digitsOf, maskOf } from "../candidates";
import { intersection, unitName, type Constraint, type ConstraintSet } from "../constraints";
import type { Context, Elimination, Step, Technique } from "./types";

interface OverlapPair {
  readonly a: Constraint;
  readonly b: Constraint;
  readonly shared: readonly number[];
  readonly bOnly: readonly number[];
}

const overlapCache = new WeakMap<ConstraintSet, OverlapPair[]>();

/**
 * Ordered pairs of units that overlap in at least two cells but are not
 * identical. Computed once per constraint set -- it is fixed geometry.
 */
function overlappingPairs(cs: ConstraintSet): OverlapPair[] {
  let pairs = overlapCache.get(cs);
  if (pairs) return pairs;

  pairs = [];
  for (const a of cs.constraints) {
    for (const b of cs.constraints) {
      if (a === b) continue;
      const shared = intersection(a, b);
      if (shared.length < 2 || shared.length === a.cells.length) continue;
      const inShared = new Set(shared);
      pairs.push({ a, b, shared, bOnly: b.cells.filter((cell) => !inShared.has(cell)) });
    }
  }
  overlapCache.set(cs, pairs);
  return pairs;
}

function findLocked(ctx: Context, wantPointing: boolean): Step | null {
  const { board, candidates, cs } = ctx;

  for (const { a, b, shared, bOnly } of overlappingPairs(cs)) {
    // "Pointing" is the box-locked case; everything else is "claiming".
    if ((a.kind === "box") !== wantPointing) continue;

    let placed = 0;
    for (const cell of a.cells) {
      if (board[cell] !== EMPTY) placed |= maskOf(board[cell]!);
    }

    for (const digit of digitsOf(~placed & 0b111111111)) {
      const bit = maskOf(digit);

      // Every remaining home for `digit` in A must lie in the overlap...
      let inShared = 0;
      let outside = false;
      for (const cell of a.cells) {
        if ((candidates[cell]! & bit) === 0) continue;
        if (shared.includes(cell)) inShared++;
        else {
          outside = true;
          break;
        }
      }
      if (outside || inShared < 1) continue;

      // ...and the deduction is only a step if it removes something from B.
      const eliminations: Elimination[] = [];
      for (const cell of bOnly) {
        if (candidates[cell]! & bit) eliminations.push({ cell, digit: digit as Digit });
      }
      if (eliminations.length === 0) continue;

      const cells = shared.filter((cell) => candidates[cell]! & bit);
      return {
        technique: wantPointing ? "pointing" : "claiming",
        pattern: cells,
        digits: [digit],
        placements: [],
        eliminations,
        explanation: wantPointing
          ? `Pointing: in ${unitName(a)}, ${digit} can only go in ${unitName(b)} ` +
            `(${cells.map(cellName).join(", ")}). Wherever it lands it uses up ${digit} for ` +
            `${unitName(b)}, so ${digit} can be removed from the rest of ${unitName(b)}.`
          : `Box-Line Reduction: in ${unitName(a)}, ${digit} can only go in ${unitName(b)} ` +
            `(${cells.map(cellName).join(", ")}), so ${digit} can be removed from the rest ` +
            `of ${unitName(b)}.`,
      };
    }
  }
  return null;
}

export const pointing: Technique = {
  id: "pointing",
  name: "Pointing Pair/Triple",
  tier: "medium",
  ser: 2.6,
  find: (ctx) => findLocked(ctx, true),
};

export const claiming: Technique = {
  id: "claiming",
  name: "Box-Line Reduction",
  tier: "medium",
  ser: 2.8,
  find: (ctx) => findLocked(ctx, false),
};
